import React, { useEffect, useState } from 'react';
import SideNavigationBar from '../../components/SideNavigationBar';
import { Grid, Typography, CircularProgress, Box } from '@mui/material';
import { helperText, useMainCustomStyle } from '../../assets/theme/Theme';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
	addFeedBackToPlan,
	cancelApiRequest,
	connectWithApi,
	fetchCurrentPlan,
	generateCarePlan,
} from '../../services/CarePlanServices';
import { PlanDocument } from '../../components/PDFDocument';
import { pdf } from '@react-pdf/renderer';
import { saveAs } from 'file-saver';
import SearchDropDown from '../../components/SearchDropDown';
import GeneratedPlanTemplate from './GeneratedPlanTemplate';
import OptionPopup from '../../components/OptionPopup';
import { v4 as uuidv4 } from 'uuid';
import {
	getEnabledUserFeatures,
	getOptimizedCarePlan,
	getOptimizedPlanData,
} from '../../context/MainProvider';
import { useLocation, useNavigate } from 'react-router-dom';
import IconButton from '../../components/IconButton';
import UserLimitationPopup from '../../components/UserLimitationPopup';
import { FEATURE_TYPES } from '../../Utils/Enums';
import {
	getAccessToken,
	getElderHomeId,
	getUserId,
	getUserName,
} from '../../Utils/AuthUtil';
import {
	addOptimizedCarePlan,
	fetchCarePlanById,
	fetchExternalElderPlan,
	fetchExternalLatestElderPlanData,
	fetchLatestElderPlanData,
	fetchLatestInitialPlan,
	fetchLatestInitialPlanPlan,
	getResidentIds,
	updateCarePlan,
	updateExternalPlanOptimizedFlag,
} from '../../services/ElderService';
import { StarIcon } from '../../assets/icons/Icons';
import ToastBanner from '../../components/ToastBanner';
import { sanitizeTitle } from '../../Utils/Helpers';
import AlertBanner from '../../components/AlertBanner';

// eslint-disable-next-line no-undef
const central_db_host = process.env.REACT_APP_UMS_API_URL;

const INITIAL_FEEDBACK_ERROR_STATE = {
	itemIndex: -1,
	status: false,
	msg: '',
};

const ERROR_STATE = {
	status: false,
	msg: '',
};

const sectionOrder = ['Current Situation', 'Goals', 'Action Description'];

function CreateCarePlan() {
	const { state } = useLocation();
	const navigate = useNavigate();
	const mainDivStyle = useMainCustomStyle();
	const token = getAccessToken();
	const homeId = getElderHomeId();
	const { optimizedCarePlan, storeOptimizedCarePlan } =
		getOptimizedCarePlan();
	const { optimizedPlanData, storeOptimizedPlanData } =
		getOptimizedPlanData();
	const [id, setId] = useState(null);
	const [sessionId, setSessionId] = useState('');
	const [idError, setIdError] = useState({ status: false, msg: '' });
	const [currentPlan, setCurrentPlan] = useState([]);
	const [isCurrentPlanFetched, setIsCurrentPlanFetched] = useState(false);
	const [currentPlanData, setCurrentPlanData] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [txt, setTxt] = useState([]);
	const [fileData, setFileData] = useState([]);
	const [txtError, setTxtError] = useState({ status: false, msg: '' });
	const [popupBtnState, setPopupBtnState] = useState('');
	const [isSubPlanLoading, setIsSubPlanLoading] = useState({
		loading: false,
		planIndex: '',
	});
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [popupTitle, setPopupTitle] = useState('');
	const [isPopupTypeUpload, setIsPopupTypeUpload] = useState('API');
	const [values, setValues] = useState({ url: '', token: '' });
	const [residentIds, setResidentIds] = useState([]);
	const [residentFetchedIds, setResidentFetchedIds] = useState([]);
	const [apiUrlError, setApiUrlError] = useState({ status: false, msg: '' });
	const [apiTokenError, setApiTokenError] = useState(ERROR_STATE);
	const [elderDetails, setElderDetails] = useState({});
	const [expandedAccordion, setExpandedAccordion] = useState(null);
	const [planType, setPlanType] = useState('Current Plan');
	const [feedbackArr, setFeedbackArr] = useState([]);
	const [feedbackError, setFeedbackError] = useState(
		INITIAL_FEEDBACK_ERROR_STATE,
	);
	const [openLimitationModal, setOpenLimitationModal] = useState(false);
	const { enabledUserFeatureList } = getEnabledUserFeatures();
	const isThisFeatureDisabled = !enabledUserFeatureList.includes(
		FEATURE_TYPES[3].key,
	);
	const [onShowFileUpload, setOnShowFileUpload] = useState(false);
	const [externalApiData, setExternalApiData] = useState([]);
	const [noReviewError, setNoReviewError] = useState(
		INITIAL_FEEDBACK_ERROR_STATE,
	);
	const [loadingMsg, setLoadingMsg] = useState('');
	const [isGenerationComplete, setIsGenerationComplete] = useState(false);
	const [showNoResidentBanner, setShowNoResidentBanner] = useState(false);
	const [navStateValues, setNavStateValues] = useState({});
	const [mainFeedback, setMainFeedback] = useState('');
	const [mainFeedbackError, setMainFeedbackError] = useState(ERROR_STATE);
	const [isOptimizingWholePlan, setIsOptimizingWholePlan] = useState(false);
	const [mainFeedbackFiles, setMainFeedbackFiles] = useState([]);
	const [feedbackFiles, setFeedbackFiles] = useState([]);
	const normalize = str => str?.toLowerCase().replace(/\s+/g, '').trim();

	useEffect(() => {
		const residentId = localStorage.getItem('resident_id');
		setId(residentId);
		fetchResidentIds();
	}, []);

	useEffect(() => {
		if (
			!state?.isUpdatingPageState &&
			optimizedCarePlan &&
			optimizedCarePlan.formattedPlan.length > 0
		) {
			setCurrentPlan(optimizedCarePlan.formattedPlan);
			setCurrentPlanData(optimizedCarePlan.fullPlan);
			setId(optimizedPlanData.id);
			setTxt(optimizedPlanData.file);
			setSessionId(optimizedPlanData.sessionId);
			setIsCurrentPlanFetched(true);
		}
	}, []);

	useEffect(() => {
		if (state?.id && state?.elderName && state?.fromDashboard) {
			onChangeSelection('', state.elderName);
			setNavStateValues(state);
			navigate(location.pathname, {
				replace: true,
				state: {
					...state,
					id: null,
					elderName: null,
					isExternalElder: false,
					exPlanId: null,
					fromDashboard: false,
				},
			});
		}
	}, []);

	async function fetchResidentIds() {
		const response = await getResidentIds(homeId, token);

		if (response.message === 'Success') {
			const newIds = response.content.map(item =>
				item.isFromExternalApi
					? item.name
					: `${item.elderId} (${item.name})`,
			);

			setResidentFetchedIds(response.content);
			setResidentIds(newIds);
			setShowNoResidentBanner(response.content.length > 0 ? false : true);
		} else {
			toast.error(response.message);
		}
	}

	function validateUploadInputs() {
		let isValid = true;

		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select resident id' });
			isValid = false;
		}

		if (txt.length === 0) {
			setTxtError({ status: true, msg: 'Upload the pdf file' });
			isValid = false;
		}

		return isValid;
	}

	function validateApiInputs() {
		let isValid = true;

		if (!values.url || values.url === '') {
			setApiUrlError({ status: true, msg: 'Enter api url' });
			isValid = false;
		}

		if (!values.token || values.token === '') {
			setApiTokenError({ status: true, msg: 'Enter api token' });
			isValid = false;
		}

		return isValid;
	}

	async function onFetchCurrentPlanFromDB(
		isExternalElder = false,
		elder = null,
	) {
		const elderId = isExternalElder ? elder.elderId : elder.id;

		setLoadingMsg('Retrieving current plan');
		setIsLoading(true);
		let response;
		let optimizedPlanIsEx = false;
		if (isExternalElder) {
			const planDetails = await fetchExternalLatestElderPlanData(
				homeId,
				elderId,
				token,
			);
			optimizedPlanIsEx = planDetails.isPlanExternal;
			if (planDetails.isPlanExternal) {
				response = await fetchExternalElderPlan(planDetails.id, token);
			} else {
				response = await fetchCarePlanById(
					planDetails.id,
					token,
					'optimized',
				);
			}
		} else {
			const manualPlanDetails = await fetchLatestElderPlanData(
				elderId,
				token,
			);
			response = await fetchCarePlanById(
				manualPlanDetails.id,
				token,
				manualPlanDetails.planType === 'optimized'
					? 'optimized'
					: 'initial',
			);
		}

		if (!response.error) {
			const planData = cleanCarePlan(
				optimizedPlanIsEx ? response.carePlan.plan : response.plan,
			);
			setCurrentPlan(planData);
			setCurrentPlanData(convertPlan(planData));
			setIsCurrentPlanFetched(true);
			onCloseModal();
			storeOptimizedPlanData(id, sessionId, txt);
			storeOptimizedCarePlan(planData, planData);
		} else {
			toast.error(response.message);
		}

		setIsLoading(false);
		setLoadingMsg('');
	}

	function cleanCarePlan(plan) {
		if (!Array.isArray(plan)) return [];

		const sectionOrder = [
			'Current Situation',
			'Goals',
			'Action Description',
		];

		return plan
			.filter(item => item?.bodyContent?.length > 0)
			.map(item => {
				const orderedBodyContent = [
					...sectionOrder
						.map(key =>
							item.bodyContent.find(
								sub => normalize(sub.label) === normalize(key),
							),
						)
						.filter(Boolean),

					...item.bodyContent.filter(
						sub =>
							!sectionOrder.some(
								key => normalize(sub.label) === normalize(key),
							),
					),
				];

				return {
					title: item?.title,
					bodyContent: orderedBodyContent,
				};
			});
	}

	async function onFetchCurrentPlan(manualSelection = true, elder = null) {
		if (manualSelection) {
			const isInputsValid = validateUploadInputs();
			if (!isInputsValid) {
				return;
			}
		}

		setIsLoading(true);

		if (manualSelection && !elder) {
			const formData = new FormData();

			const selectedElder = residentFetchedIds.find(
				item => item.elderId === id.split(' ')[0],
			);

			formData.append('elder_id', selectedElder.id);
			fileData.forEach(file => {
				formData.append(
					'care_plan_pdf',
					new Blob([file], { type: 'text/plain' }),
					file.name,
				);
			});
			formData.append('use_api', false);
			formData.append('session_id', sessionId ? sessionId : uuidv4());
			formData.append('base_url', central_db_host);
			formData.append('auth_token', token);
			formData.append('client_id', getElderHomeId());

			const data = await fetchCurrentPlan(formData);
			if (!data.error) {
				const longTermPlan = data.current_plan.long_term_plan;
				const formattedPlan = longTermPlan.map(item => {
					return {
						title: item.title,
						bodyContent: Object.keys(item)
							.filter(
								value =>
									![
										'last_updated',
										'last_reviewed',
										'title',
									].includes(value) && item[value].length > 0,
							)
							.map(value => {
								return {
									label: sanitizeTitle(value),
									content: item[value],
									enableUserPrompt: false,
								};
							}),
					};
				});

				setCurrentPlan(formattedPlan);
				setCurrentPlanData(data);
				setIsCurrentPlanFetched(true);
				setIsLoading(false);
				onCloseModal();
				storeOptimizedPlanData(id, sessionId, txt);
				storeOptimizedCarePlan(formattedPlan, data);
			} else {
				toast.error(data.message);
				setIsLoading(false);
			}
		} else {
			setLoadingMsg('Generating Current plan');
			const updateOptimizedPlan = planData => {
				if (
					planData.message &&
					!planData.message.startsWith('Stream')
				) {
					setLoadingMsg(planData.message);
				}

				if (planData.message === 'Done') {
					const longTermPlan =
						planData.data.current_plan.long_term_plan;
					const formattedPlan = longTermPlan.map(item => {
						return {
							title: item.title,
							bodyContent: Object.keys(item)
								.filter(
									value =>
										![
											'last_updated',
											'last_reviewed',
											'title',
										].includes(value) &&
										item[value].length > 0,
								)
								.map(value => {
									return {
										label: sanitizeTitle(value),
										content: item[value],
										enableUserPrompt: false,
									};
								}),
						};
					});

					setCurrentPlan(formattedPlan);
					setCurrentPlanData(planData.data);
					setIsCurrentPlanFetched(true);
					setPlanType('Current Plan');
					setIsGenerationComplete(true);
					setLoadingMsg('Current care plan is created');
					setTimeout(() => {
						onResetStreamLoaders();
					}, 3000);
				}
			};

			const data = {
				elder_id: elder.elderId,
				use_api: true,
				session_id: sessionId ? sessionId : uuidv4(),
				base_url: central_db_host,
				auth_token: token,
				client_id: getElderHomeId(),
			};

			await fetchCurrentPlan(data, true, updateOptimizedPlan);
		}
	}

	async function storeOptimizedPlanInDB() {
		let selectedElder;
		if (state && state.fromDashboard) {
			selectedElder = {
				id: state.id,
				elderId: state.elderId,
				exPlanId: state.exPlanId,
			};
		} else {
			selectedElder = residentFetchedIds.find(
				item => item.elderId === id.split(' ')[0] || item.name === id,
			);
		}

		const planData = {
			elderHomeId: homeId,
			elderId: selectedElder.id,
			generatedUserId: getUserId(),
			generatedUserName: getUserName(),
			plan: currentPlan,
		};

		const formData = new FormData();
		formData.append(
			'plan',
			new Blob([JSON.stringify(planData)], {
				type: 'application/json',
			}),
		);

		fileData.forEach(file => {
			formData.append('file', file);
		});

		const response = await addOptimizedCarePlan(formData, token);

		if (!response.error) {
			toast.success('Care plan saved successfully');

			if (navStateValues && navStateValues.exPlanId) {
				if (navStateValues.isExternalElder) {
					updateExternalPlanOptimizedFlag(
						navStateValues.exPlanId,
						token,
					);
				} else {
					const data = { optimized: true };
					updateCarePlan(
						navStateValues.exPlanId,
						data,
						token,
						'initial',
					);
				}
			}
		} else {
			toast.error(response.message);
		}
	}

	const formatEnhancedCarePlanItem = (item, value) => {
		if (item === 'reviews') {
			return value[item].map(val => `${val}`);
		}
		return value[item];
	};

	//TODO: This function need to be updated to optimize entire plan
	async function onOptimizeCarePlan() {
		if (
			(!mainFeedback || mainFeedback.trim() === '') &&
			(!mainFeedbackFiles || mainFeedbackFiles.length === 0)
		) {
			setMainFeedbackError({
				status: true,
				msg: 'Please add instruction/file.',
			});
			return;
		}
		setIsLoading(true);
		setLoadingMsg('Generating plan');
		setIsOptimizingWholePlan(true);
		setExpandedAccordion(null);

		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const formData = new FormData();
		formData.append('instructions', mainFeedback);
		formData.append(
			'whole_plan',
			JSON.stringify({ plan: currentPlanData.long_term_plan }),
		);
		formData.append('use_api', false);
		if (mainFeedbackFiles.length > 0) {
			mainFeedbackFiles.forEach(file => {
				formData.append(
					'file',
					new Blob([file], { type: 'text/plain' }),
					file.name,
				);
			});
		}

		const data = await generateCarePlan(formData);

		if (!data.error) {
			if (data.refined_whole_plan) {
				const modifiedSections =
					data.refined_whole_plan.changes.categories;
				const wholePlan = data.refined_whole_plan.whole_plan.plan;

				const updatedPlan = currentPlan.map((value, planIndex) => {
					const sectionData =
						modifiedSections?.[wholePlan[planIndex].title];
					const subPlans = sectionData?.subplans || {};

					return {
						title: wholePlan[planIndex].title,
						bodyContent: Object.keys(wholePlan[planIndex])
							.filter(
								item =>
									![
										'last_updated',
										'last_reviewed',
										'title',
										'reasoning',
									].includes(item) &&
									Array.isArray(wholePlan[planIndex][item]) &&
									wholePlan[planIndex][item].length > 0,
							)
							.map(item => {
								return {
									label: sanitizeTitle(item),
									content: formatEnhancedCarePlanItem(
										item,
										wholePlan[planIndex],
									),
								};
							}),
						feedback: '',
						modifiedSection: getModifiedSectionData(
							wholePlan[planIndex],
							subPlans,
						),
						isPlanModified: hasModifications(subPlans),
						enableUserPrompt: false,
					};
				});

				setCurrentPlan(updatedPlan);
				setPlanType('Optimized Plan');
				setIsSubPlanLoading({
					loading: false,
					planIndex: '',
				});
				storeOptimizedCarePlan(updatedPlan, currentPlanData);
				storeOptimizedPlanData(id, sessionId, txt);
				setNoReviewError(INITIAL_FEEDBACK_ERROR_STATE);
				setFeedbackError(INITIAL_FEEDBACK_ERROR_STATE);
				setMainFeedbackError(ERROR_STATE);
				setFeedbackFiles([]);
				setMainFeedbackFiles([]);
				setMainFeedback('');
			} else {
				setIsSubPlanLoading(prevState => ({
					...prevState,
					loading: false,
				}));
				toast.error(data.error);
			}
		} else {
			toast.error(data.message);
			setIsLoading(false);
		}

		setIsLoading(false);
		setIsOptimizingWholePlan(false);
	}

	function hasModifications(subPlans) {
		if (!subPlans) return false;

		return Object.values(subPlans).some(section => {
			return (
				(section.added && section.added.length > 0) ||
				(section.modified && section.modified.length > 0)
			);
		});
	}

	async function onAddFeedBackToPlan(index) {
		if (
			(!feedbackArr[index] || feedbackArr[index].trim() === '') &&
			(!feedbackFiles[index] || feedbackFiles[index].length === 0)
		) {
			setFeedbackError({
				itemIndex: index,
				status: true,
				msg: 'Please add instruction/file.',
			});
			return;
		}

		setIsSubPlanLoading({
			loading: true,
			planIndex: index,
		});

		// const subPlan = currentPlanData.current_plan
		// 	? currentPlanData.current_plan.long_term_plan[index]
		// 	: currentPlanData.refined_whole_plan.long_term_plan[index];

		const subPlan = currentPlanData.long_term_plan[index];

		const selectedElder = residentFetchedIds.find(
			item => item.elderId === id.split(' ')[0] || item.name === id,
		);

		const formData = new FormData();
		formData.append('elder_id', selectedElder.id);
		formData.append('session_id', sessionId);
		formData.append('sub_plan', JSON.stringify([subPlan]));
		formData.append('instructions', feedbackArr[index]);
		formData.append('use_api', onShowFileUpload ? false : true);
		if (feedbackFiles[index] && feedbackFiles[index].length > 0) {
			feedbackFiles[index].forEach(file => {
				formData.append(
					'file',
					new Blob([file], { type: 'text/plain' }),
					file.name,
				);
			});
		}

		const responseData = await addFeedBackToPlan(formData);
		const data = responseData.refined_plan;

		if (data.section) {
			const modifiedSections = data.changes;
			const updatedPlan = currentPlan.map((value, planIndex) => {
				if (index == planIndex) {
					return {
						title: data.section.title,
						bodyContent: Object.keys(data.section)
							.filter(
								item =>
									![
										'last_updated',
										'last_reviewed',
										'title',
										'reasoning',
									].includes(item) &&
									data.section[item].length > 0,
							)
							.map(item => {
								return {
									label: sanitizeTitle(item),
									content: formatEnhancedCarePlanItem(
										item,
										data.section,
									),
								};
							}),
						feedback: '',
						modifiedSection: getModifiedSectionData(
							data.section,
							modifiedSections,
						),
						isPlanModified: true,
						enableUserPrompt: false,
					};
				}

				return value;
			});

			setCurrentPlan(updatedPlan);
			setPlanType('Optimized Plan');
			setIsSubPlanLoading({
				loading: false,
				planIndex: '',
			});
			handlePlanFeedbackChange('', index);
			storeOptimizedCarePlan(updatedPlan, currentPlanData);
			storeOptimizedPlanData(id, sessionId, txt);
			setNoReviewError(INITIAL_FEEDBACK_ERROR_STATE);
			setFeedbackError(INITIAL_FEEDBACK_ERROR_STATE);
			setFeedbackFiles([]);
		} else if (data.requires_feedback) {
			const updatedPlan = currentPlan.map((value, planIndex) => {
				if (index == planIndex) {
					return {
						...value,
						enableUserPrompt: true,
					};
				}

				return value;
			});

			setCurrentPlan(updatedPlan);
			setIsSubPlanLoading(prevState => ({
				...prevState,
				loading: false,
			}));

			setNoReviewError({
				itemIndex: index,
				status: true,
				msg: `${data.message}`,
			});
		} else {
			setIsSubPlanLoading(prevState => ({
				...prevState,
				loading: false,
			}));
			toast.error(data.error);
		}
	}

	function getModifiedSectionData(mainSection, currentSubSection) {
		const modifiedData = [];

		Object.keys(currentSubSection).map(section => {
			const dataBySection = [];

			if (currentSubSection[section].modified.length > 0) {
				currentSubSection[section].modified.map(val => {
					if (mainSection[section][val]) {
						dataBySection.push(mainSection[section][val]);
					}
				});
			}

			if (currentSubSection[section].added.length > 0) {
				currentSubSection[section].added.map(val => {
					if (mainSection[section][val]) {
						dataBySection.push(mainSection[section][val]);
					}
				});
			}

			modifiedData.push(dataBySection);
		});

		return modifiedData;
	}

	async function onGeneratePdf() {
		const fileName = `SenzCare.AI_OptimizedCarePlan-${id}.pdf`;
		const elderId = onShowFileUpload ? id.split(' ')[0] : 'N/A';
		const name = onShowFileUpload ? id.split('(')[1].split(')')[0] : id;

		const blob = await pdf(
			<PlanDocument
				elderId={elderId}
				elderName={name}
				documentData={currentPlan}
				title={'Optimized Care Plan'}
			/>,
		).toBlob();
		saveAs(blob, fileName);
	}

	function readTextFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0].size && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setTxt([...txt, e[0]]);
		setTxtError({ status: false, msg: '' });
		setFileData([...fileData, e[0]]);
	}

	function readFeedBackFile(e, mainIndex) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setTxtError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setTxtError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0].size && e[0].size > 5242880) {
			setTxtError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setFeedbackError(INITIAL_FEEDBACK_ERROR_STATE);
		setFeedbackFiles(prev => {
			const updated = [...prev];

			while (updated.length <= mainIndex) {
				updated.push([]);
			}

			updated[mainIndex] = [...updated[mainIndex], e[0]];

			return updated;
		});
	}

	function readMainFeedbackFile(e) {
		const ext = e[0].path.split('.').pop().toLowerCase();

		if (!['txt', 'pdf', 'docx', 'xlsx'].includes(ext)) {
			setMainFeedbackError({ status: true, msg: 'Invalid file format.' });
			return;
		}

		if (e[0] && e[0].size === 0) {
			setMainFeedbackError({
				status: true,
				msg: 'The selected file is empty.',
			});
			return;
		}

		if (e[0].size && e[0].size > 5242880) {
			setMainFeedbackError({
				status: true,
				msg: 'Maximum upload file size is 5MB.',
			});
			return;
		}

		setMainFeedbackFiles([...mainFeedbackFiles, e[0]]);
		setMainFeedbackError(ERROR_STATE);
	}

	function onRemoveFiles(content) {
		const clone = fileData.filter(value => value.name !== content.name);
		setTxt(clone);
		setFileData(clone);
	}

	function onRemoveMainFeedbackFiles(content) {
		const clone = mainFeedbackFiles.filter(
			value => value.name !== content.name,
		);
		setMainFeedbackFiles(clone);
	}

	function onRemoveFeedbackFiles(content, mainIndex) {
		setFeedbackFiles(prev => {
			const updated = [...prev];

			const files = updated[mainIndex] || [];

			updated[mainIndex] = files.filter(
				file => file.name !== content.name,
			);

			return updated;
		});
	}
	function convertBulletStringToArray(str) {
		return str.split('\n').map(line => line.replace(/^•\s*/, ''));
	}

	function handlePlanChange(value, mainIndex, itemIndex) {
		const updatedItems = convertBulletStringToArray(value);
		const clone = currentPlan.map((item, index) => {
			if (index === mainIndex) {
				return {
					...item,
					bodyContent: item.bodyContent.map((val, i) => {
						if (i === itemIndex) {
							return {
								...val,
								content: updatedItems,
								isPlanUpdated: true,
							};
						}

						return val;
					}),
				};
			}

			return item;
		});

		setCurrentPlan(clone);
	}

	function handlePlanFeedbackChange(value, mainIndex) {
		let clone = [...feedbackArr];
		clone[mainIndex] = value;
		setFeedbackArr(clone);
		setFeedbackError(INITIAL_FEEDBACK_ERROR_STATE);
	}

	function onClickPopupBtn() {
		if (popupBtnState === 'Upload') {
			onFetchCurrentPlan();
		} else {
			onConnectWithApi();
		}
	}

	function onPressMainButtons(type) {
		let isValueAdded = true;

		if (!id || id === '') {
			setIdError({ status: true, msg: 'Select resident id' });
			isValueAdded = false;
		}

		if (isValueAdded) {
			if (type === 'UPLOAD') {
				setPopupTitle('Upload files');
				setIsPopupTypeUpload(true);
				setPopupBtnState('Upload');
			} else {
				setPopupTitle('Connect API');
				setIsPopupTypeUpload(false);
				setPopupBtnState('Connect');
			}

			setIsModalOpen(true);
		}
	}

	function onChangeConnectApi(name, value) {
		if (name === 'url') {
			setApiUrlError({ status: false, msg: '' });
		}
		if (name === 'token') {
			setApiTokenError({ status: false, msg: '' });
		}

		setValues(values => ({ ...values, [name]: value }));
	}

	async function onConnectWithApi() {
		const isInputsValid = validateApiInputs();
		if (!isInputsValid) {
			return;
		}

		const response = await connectWithApi(values.url, values.token, id);

		//TODO: will get the care plan as response from the api enter by the user
		onCloseModal();
	}

	function onCloseModal() {
		setIsModalOpen(false);
		setPopupBtnState('');
		setIsLoading(false);
		setValues({ url: '', token: '' });
		setExpandedAccordion(null);
	}

	function onCreateNewPlan(restAll = true) {
		if (restAll) {
			setId(null);
			setSessionId('');
		}
		setCurrentPlan([]);
		setTxt([]);
		setFileData([]);
		setExpandedAccordion(null);
		setIsCurrentPlanFetched(false);
		setOnShowFileUpload(false);
		onResetStreamLoaders();
	}

	const handleAccordionExpansion = id => (_, isExpanded) => {
		setExpandedAccordion(isExpanded ? id : false);
	};

	function onResetStreamLoaders() {
		setIsLoading(false);
		setLoadingMsg('');
		setIsGenerationComplete(false);
	}

	function onChangeSelection(e, value) {
		setId(value);
		setSessionId(uuidv4());
		setIdError({ status: false, msg: '' });
		onCreateNewPlan(false);
		onResetStreamLoaders();

		let selectedElder;
		if (state && state.fromDashboard) {
			selectedElder = {
				id: state.id,
				elderId: state.elderId,
				exPlanId: state.exPlanId,
			};
		} else {
			selectedElder = residentFetchedIds.find(
				item =>
					item.elderId === value.split(' ')[0] || item.name === value,
			);
		}

		if (selectedElder?.isFromExternalApi || state?.fromDashboard) {
			setOnShowFileUpload(false);
			onFetchCurrentPlanFromDB(true, selectedElder);
		} else {
			setOnShowFileUpload(false);
			onFetchCurrentPlanFromDB(false, selectedElder);
		}
	}

	function convertPlan(data) {
		const plan = data || [];

		const extract = (section, label) =>
			section.bodyContent?.find(
				b => normalize(b.label) === normalize(label),
			)?.content || [];

		return {
			long_term_plan: plan.map(section => ({
				title: section.title,
				['Action Description']: extract(section, 'Action Description'),
				['Goals']: extract(section, 'Goals'),
				['Current Situation']: extract(section, 'Current Situation'),
			})),
		};
	}

	return (
		<>
			<SideNavigationBar />
			<div style={mainDivStyle}>
				<Grid
					container
					paddingLeft={{ xs: '0px', md: '20px', sm: '20px' }}
					paddingRight={'20px'}>
					<Grid item paddingLeft="30px" paddingBottom={'20px'}>
						<Typography
							variant="h6"
							fontWeight={'600'}
							color="#28696D">
							Optimize Care Plan
						</Typography>
					</Grid>
					{showNoResidentBanner && (
						<Grid
							container
							sx={{
								borderRadius: '5px',
								paddingLeft: '30px',
								paddingBottom: '10px',
							}}>
							<AlertBanner
								content={
									'No residents available. Add residents to get started.'
								}
								btnOneTxt={'Manual Onboarding'}
								btnTwoTxt={'Connect Api'}
								handleButtonOneClick={() =>
									navigate('/addResident')
								}
								handleButtonTwoClick={() =>
									navigate('/integration')
								}
								displayBtnOne={true}
								displayBtnTwo={true}
							/>
						</Grid>
					)}
					<Grid
						container
						marginLeft={'30px'}
						bgcolor={'#FFFFFF'}
						borderRadius={'10px'}
						style={
							isThisFeatureDisabled
								? {
										filter: 'blur(0.8px)',
										pointerEvents: 'none',
									}
								: {}
						}>
						<Grid
							container
							paddingLeft={'20px'}
							justifyContent={'space-between'}>
							<Grid
								item
								xs={12}
								sm={4}
								paddingRight={'10px'}
								paddingTop={'20px'}
								paddingBottom={'5px'}>
								<Typography
									fontSize={'16px'}
									color={'#7C7C7C'}
									paddingBottom="10px">
									Elder ID
								</Typography>
								<Box
									display="flex"
									alignItems="center"
									width="100%">
									<SearchDropDown
										value={id}
										dropDownData={residentIds}
										onChange={onChangeSelection}
										placeholder={'Select Elder Id here...'}
										error={idError.status}
										fullWidth
										disable={isLoading}
									/>
								</Box>
								<Typography style={helperText}>
									{idError.msg}
								</Typography>
							</Grid>
							{isCurrentPlanFetched && (
								<Grid padding={'10px'}>
									<IconButton
										text={'Optimize New Plan'}
										onClick={onCreateNewPlan}
									/>
								</Grid>
							)}
						</Grid>
						<Grid
							container
							paddingLeft={'20px'}
							paddingRight={'10px'}
							paddingTop={'5px'}>
							<GeneratedPlanTemplate
								data={currentPlan}
								feedbacks={feedbackArr}
								isPlanEditable={true}
								onPlanChange={handlePlanChange}
								onChangeFeedback={handlePlanFeedbackChange}
								onAddFeedBackToPlan={onAddFeedBackToPlan}
								isSubPlanLoading={isSubPlanLoading}
								elderData={elderDetails}
								expanded={expandedAccordion}
								handleExpansion={handleAccordionExpansion}
								onClickFirstBtn={onPressMainButtons}
								popupBtnState={popupBtnState}
								isCurrentPlanFetched={isCurrentPlanFetched}
								file={txt}
								onRemoveFiles={onRemoveFiles}
								planType={planType}
								feedbackError={feedbackError}
								onGeneratePdf={onGeneratePdf}
								onShowFileUpload={onShowFileUpload}
								noReviewError={noReviewError}
								onStoreOptimizedPlan={storeOptimizedPlanInDB}
								mainFeedback={mainFeedback}
								onChangeMainFeedback={value => {
									setMainFeedback(value);
									setMainFeedbackError(ERROR_STATE);
								}}
								mainFeedbackError={mainFeedbackError}
								onOptimizePlan={onOptimizeCarePlan}
								isOptimizingWholePlan={isOptimizingWholePlan}
								mainFeedbackFile={mainFeedbackFiles}
								onChangeMainFeedbackFile={readMainFeedbackFile}
								onRemoveMainFeedbackFiles={
									onRemoveMainFeedbackFiles
								}
								onChangeFeedbackFile={readFeedBackFile}
								onRemoveFeedbackFiles={onRemoveFeedbackFiles}
								feedbackFiles={feedbackFiles}
							/>
						</Grid>
					</Grid>
				</Grid>
			</div>
			<OptionPopup
				isModalOpen={isModalOpen}
				readTextFile={readTextFile}
				txt={txt}
				txtError={txtError}
				isCurrentPlanFetched={isCurrentPlanFetched}
				onClickBtn={onClickPopupBtn}
				values={values}
				onChange={onChangeConnectApi}
				isTypeUpload={isPopupTypeUpload}
				popupTitle={popupTitle}
				onClose={() => {
					onCloseModal();
					cancelApiRequest();
				}}
				popupBtnState={popupBtnState}
				apiError={{
					url: apiUrlError,
					token: apiTokenError,
				}}
				onRemoveFiles={onRemoveFiles}
				loading={isLoading}
			/>
			<UserLimitationPopup
				isOpen={openLimitationModal}
				onClose={() => setOpenLimitationModal(false)}
				text={'0 of 5 Care plan generations'}
				progress={50}
			/>
			{isLoading && !isModalOpen && (
				<ToastBanner
					open={isLoading}
					message={loadingMsg}
					icon={<StarIcon color={'#FFFFFF'} />}
					isCompleted={isGenerationComplete}
				/>
			)}
		</>
	);
}

export default CreateCarePlan;
